import * as React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  FormHelperText,
  Typography,
} from '@mui/material';

import styles from './ReportTypeSelector.module.css';

export interface ReportTypeOption<T extends string = string> {
  value: T;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

export interface ReportTypeSelectorProps<T extends string = string> {
  options: ReadonlyArray<ReportTypeOption<T>>;
  value: T | null;
  onChange: (value: T) => void;
  error?: string;
  label?: string;
}

/** Large selectable cards behaving as a radio group (arrow keys move between them). */
function ReportTypeSelector<T extends string>({
  options,
  value,
  onChange,
  error,
  label = 'Report type',
}: ReportTypeSelectorProps<T>) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + options.length) % options.length;
    refs.current[next]?.focus();
    onChange(options[next].value);
  };

  return (
    <Box>
      <Box role="radiogroup" aria-label={label} className={styles.grid}>
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <Card
              key={option.value}
              variant="outlined"
              className={styles.card}
              sx={{
                borderColor: selected ? 'primary.main' : undefined,
                borderWidth: selected ? 2 : 1,
                bgcolor: selected ? 'action.selected' : undefined,
              }}
            >
              <CardActionArea
                ref={(el) => (refs.current[index] = el)}
                role="radio"
                aria-checked={selected}
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => onChange(option.value)}
                onKeyDown={(e) => onKeyDown(e, index)}
                className={styles.action}
              >
                <CardContent className={styles.content}>
                  <Box className={styles.titleRow}>
                    {option.icon && (
                      <Box className={styles.icon} color="primary.main">
                        {option.icon}
                      </Box>
                    )}
                    <Typography variant="h6" component="span">
                      {option.title}
                    </Typography>
                    {selected && (
                      <CheckCircleIcon color="primary" className={styles.check} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
}

export default ReportTypeSelector;
